import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../components/AppIcon';
import { CircleFlag } from 'react-circle-flags';
import { useTranslation } from 'react-i18next';

const IntroductionSection = () => {
  const { t } = useTranslation();
  const [isMobile, setIsMobile] = useState(false);
  const titleWrapperRef = React.useRef(null);
  const titleRef = React.useRef(null);
  const [mobileTitleFontPx, setMobileTitleFontPx] = useState(30); // base ~ text-3xl
  const [mobileScale, setMobileScale] = useState(1);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Fit intro title on mobile with proper text wrapping
  useEffect(() => {
    const fitTitle = () => {
      if (!titleRef.current || !titleWrapperRef.current) return;
      if (window.innerWidth >= 640) return; // only mobile
      const containerWidth = titleWrapperRef.current.offsetWidth || 0;
      const textWidth = titleRef.current.scrollWidth || 0;
      const base = 30; // px (text-3xl)
      if (containerWidth > 0 && textWidth > 0) {
        const raw = containerWidth / textWidth; // <1 means needs shrink
        const clamped = Math.max(0.85, Math.min(1, raw));
        setMobileTitleFontPx(Math.round(base));
        setMobileScale(clamped);
      } else {
        setMobileTitleFontPx(base);
        setMobileScale(1);
      }
    };
    fitTitle();
    window.addEventListener('resize', fitTitle);
    return () => window.removeEventListener('resize', fitTitle);
  }, [t('introduction.title')]);
  
  const stories = [
    {
      name: t('introduction.fst.name'),
      role: 'local',
      location: t('introduction.fst.location'),
      tag: t('introduction.fst.tag'),
      story: t('introduction.fst.story'),
      imagePath: 'avatar_jimin.png',
      bgColor: 'bg-orange-100',
      borderColor: 'border-orange-100',
      textColor: 'text-orange-600',
      flagCode: 'kr',
      position: 'left'
    },
    {
      name: t('introduction.snd.name'),
      role: 'global',
      location: t('introduction.snd.location'),
      tag: t('introduction.snd.tag'),
      story: t('introduction.snd.story'),
      imagePath: 'avatar_sophie.png',
      bgColor: 'bg-[#daf8e7]',
      borderColor: 'border-[#daf8e7]',
      textColor: 'text-blue-600',
      flagCode: 'ch',
      position: 'right'
    },
    {
      name: t('introduction.trd.name'),
      role: 'global',
      location: t('introduction.trd.location'),
      tag: t('introduction.trd.tag'),
      story: t('introduction.trd.story'),
      imagePath: 'avatar_carlos.png',
      bgColor: 'bg-[#daf8e7]',
      borderColor: 'border-[#daf8e7]',
      textColor: 'text-green-600',
      flagCode: 'mx',
      position: 'left'
    }
  ];

  return (
    <div className="max-w-4xl mx-auto w-full px-4 sm:px-6 py-8 sm:pt-32 overflow-hidden">
      {/* Title */}
      <motion.div
        ref={titleWrapperRef}
        className="text-center mb-6 sm:mb-16 md:mb-16 max-w-[92vw] mx-auto"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        <h2
          ref={titleRef}
          className="sm:text-6xl md:text-5xl font-bold text-gray-900 break-words"
          style={{
            fontSize: isMobile ? `${mobileTitleFontPx}px` : undefined,
            lineHeight: 1.2,
            transform: isMobile ? `scale(${mobileScale})` : undefined,
            transformOrigin: 'center top',
            display: 'block',
            maxWidth: '100%',
            wordBreak: 'break-word',
            overflowWrap: 'break-word'
          }}
        >
          {t('introduction.title')}
        </h2>
      </motion.div>

      {/* Story Cards */}
      <div className="space-y-0 sm:space-y-6 md:space-y-8">
        {stories.map((story, index) => {
          const isLast = index === stories.length - 1;
          return (
          <motion.div
            key={index}
            className={`w-full flex flex-col sm:flex-row items-center gap-4 sm:gap-5 md:gap-6 ${story.position === 'right' ? 'sm:flex-row-reverse' : ''}`}
            style={!isLast && isMobile ? { marginBottom: '2rem' } : {}}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: index * 0.2 }}
            viewport={{ once: true }}
          >
            {/* Profile Section - Row1 on Mobile */}
            <div className="flex flex-row sm:flex-col items-center sm:items-center gap-2 sm:gap-1.5 md:gap-2 flex-shrink-0 w-full sm:w-auto">
              {/* Profile Image with Flag Badge */}
              <div className="relative">
                <div className={`w-[60px] h-[60px] sm:w-[70px] sm:h-[70px] md:w-[80px] md:h-[80px] rounded-full overflow-hidden flex items-center justify-center border-2 ${story.borderColor} ${story.bgColor}`}>
                  <img 
                    src={`/${story.imagePath}`}
                    alt={story.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                {/* Flag Badge overlay at avatar 5 o'clock */}
                <div className="absolute -bottom-1 -right-1 w-7 h-7 sm:w-8 sm:h-8 bg-white rounded-full flex items-center justify-center border-2 border-white shadow-md">
                  <CircleFlag countryCode={story.flagCode} height={22} />
                </div>
              </div>

              {/* Profile Info */}
              <div className="flex flex-col items-start sm:items-center gap-0.5 sm:gap-0.5 md:gap-1">
                <h3 className="text-base sm:text-base md:text-lg font-bold text-gray-900 text-left sm:text-center">
                  {story.name}
                </h3>
                <span className={`px-2 py-0.5 sm:px-2 sm:py-0.5 md:px-2.5 md:py-0.5 rounded-full text-[10px] sm:text-[11px] md:text-xs font-medium ${
                  story.role === 'local'
                    ? 'bg-orange-100 text-orange-700'
                    : 'bg-[#daf8e7] text-[#2aa99c]'
                }`}>
                  {story.role === 'local' ? t('introduction.fst.local') : t('introduction.snd.global')}
                </span>
                <div className="flex items-center gap-1 text-gray-600">
                  <Icon name="MapPin" size={12} className="text-orange-500" />
                  <span className="text-[10px] sm:text-[11px] md:text-xs">{story.location}</span>
                </div>
              </div>
            </div>

            {/* Speech Bubble Section */}
            <div className="bg-white/80 backdrop-blur-sm rounded-xl sm:rounded-xl md:rounded-2xl p-4 sm:p-4 md:p-5 border border-gray-200 shadow-sm relative flex-1 w-full">
              
              {/* Tag */}
              <div className="mb-1.5 sm:mb-2 md:mb-2">
                <span className={`inline-block px-2 py-0.5 sm:px-2.5 sm:py-1 md:px-2.5 md:py-1 rounded-full text-[10px] sm:text-[11px] md:text-xs font-medium ${story.bgColor} text-gray-700`}>
                  {story.tag}
                </span>
              </div>

              {/* Story Content */}
              <p className="text-gray-800 text-sm sm:text-sm md:text-base leading-relaxed break-words" style={{
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
                maxWidth: '100%'
              }}>
                {story.story}
              </p>
            </div>
          </motion.div>
          );
        })}
      </div>
    </div>
  );
};

export default IntroductionSection;
